import { useEffect, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { GuestReservationFormFields, GuestReservationFormProps } from './guestReservationFormTypes';

export function useGuestReservationStayDateSync(params: {
  formData: GuestReservationFormFields;
  useDuration: boolean;
  onDateChange: GuestReservationFormProps['onDateChange'];
  isUpdatingFromExternalRef: MutableRefObject<boolean>;
  isUpdatingFromSelectRef: MutableRefObject<boolean>;
  setFormData: Dispatch<SetStateAction<GuestReservationFormFields>>;
}): void {
  const { formData, useDuration, onDateChange, isUpdatingFromExternalRef, isUpdatingFromSelectRef, setFormData } =
    params;

  useEffect(() => {
    if (isUpdatingFromExternalRef.current) {
      setTimeout(() => {
        isUpdatingFromExternalRef.current = false;
      }, 0);
      return;
    }

    if (isUpdatingFromSelectRef.current) {
      setTimeout(() => {
        isUpdatingFromSelectRef.current = false;
      }, 0);
      return;
    }

    if (useDuration && formData.checkIn && formData.duration > 0) {
      const durationValue =
        typeof formData.duration === 'string' ? parseInt(formData.duration, 10) : Number(formData.duration);
      if (isNaN(durationValue) || durationValue < 1) {
        return;
      }

      const [year, month, day] = formData.checkIn.split('-').map(Number);
      const checkInDate = new Date(year, month - 1, day);
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + durationValue);

      const checkOutString = `${checkOutDate.getFullYear()}-${String(checkOutDate.getMonth() + 1).padStart(2, '0')}-${String(checkOutDate.getDate()).padStart(2, '0')}`;

      setFormData((prev) => {
        if (prev.checkOut === checkOutString && prev.duration === durationValue) {
          return prev;
        }
        return {
          ...prev,
          checkOut: checkOutString,
          duration: durationValue,
        };
      });

      if (onDateChange && checkInDate && checkOutDate) {
        const cIn = checkInDate;
        const cOut = checkOutDate;
        queueMicrotask(() => onDateChange(cIn, cOut));
      }
    }
   
  }, [formData.checkIn, formData.duration, useDuration, onDateChange]);

  useEffect(() => {
    if (isUpdatingFromExternalRef.current) {
      setTimeout(() => {
        isUpdatingFromExternalRef.current = false;
      }, 0);
      return;
    }

    if (formData.checkIn && formData.checkOut && onDateChange && !useDuration) {
      const [checkInYear, checkInMonth, checkInDay] = formData.checkIn.split('-').map(Number);
      const [checkOutYear, checkOutMonth, checkOutDay] = formData.checkOut.split('-').map(Number);
      const checkInDate = new Date(checkInYear, checkInMonth - 1, checkInDay);
      const checkOutDate = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);
      if (checkInDate && checkOutDate && checkOutDate > checkInDate) {
        const cIn = checkInDate;
        const cOut = checkOutDate;
        queueMicrotask(() => onDateChange?.(cIn, cOut));
      }
    }
   
  }, [formData.checkIn, formData.checkOut, useDuration, onDateChange]);
}
